import React from "react";
import LayoutPreviewStrip from "../LayoutEditor/LayoutPreviewStrip";
import type { LanguageOption } from "@bcc/core-renderers";
import { useLayoutPreview } from "./useLayoutPreview";

type FooterPreviewProps = {
	initialLocale?: string;
	title?: string;
	showDesktop?: boolean;
	showMobile?: boolean;
	localeOverride?: string;
	languageOptionsOverride?: LanguageOption[];
	className?: string;
};

/** Site footer preview in desktop and mobile versions. */
const FooterPreview: React.FC<FooterPreviewProps> = ({
	initialLocale,
	title = "Footer preview",
	showDesktop = true,
	showMobile = true,
	localeOverride,
	languageOptionsOverride,
	className = ""
}) => {
	const { footer, header, locale, languageOptions, loading, error } = useLayoutPreview(initialLocale, {
		localeOverride,
		languageOptionsOverride
	});

	const content = () => {
		if (loading) {
			return (
				<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
					Loading preview…
				</div>
			);
		}
		if (error) {
			return (
				<div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700">
					{error}
				</div>
			);
		}
		if (!footer) {
			return (
				<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
					No footer preview available. Make sure layout content is deployed.
				</div>
			);
		}
		return (
			<>
				{showDesktop && (
					<div className="hidden show-1840-block">
						<LayoutPreviewStrip
							mode="footer"
							headerData={header ?? undefined}
							footerData={footer}
							language={locale}
							languageOptions={languageOptions}
							defaultLanguageCode={locale}
							viewport="desktop"
							title={title}
							description="Desktop view"
						/>
					</div>
				)}
				{showMobile && (
					<LayoutPreviewStrip
						mode="footer"
						headerData={header ?? undefined}
						footerData={footer}
						language={locale}
						languageOptions={languageOptions}
						defaultLanguageCode={locale}
						viewport="mobile"
						title={title}
						description="Mobile view"
					/>
				)}
			</>
		);
	};

	return (
		<div className={className}>
			<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
				{title}
			</div>
			{content()}
		</div>
	);
};

export default FooterPreview;
